import { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast, Toaster } from "sonner";
import { Send, Megaphone, Heart, Settings, Play, Eye } from "lucide-react";

export default function AdminBroadcasts() {
    const [list, setList] = useState([]);
    const [centres, setCentres] = useState([]);
    const [form, setForm] = useState({ title: "", message: "", audience: "all", centre_id: "" });
    const [submitting, setSubmitting] = useState(false);

    // Win-back
    const [wb, setWb] = useState(null); // preview state
    const [wbSettings, setWbSettings] = useState(null);
    const [wbOpen, setWbOpen] = useState(false);
    const [wbRunning, setWbRunning] = useState(false);

    const fetchAll = async () => {
        const [b, c, p, s] = await Promise.all([
            api.get("/admin/broadcasts"),
            api.get("/centres"),
            api.get("/admin/winback/preview"),
            api.get("/admin/winback/settings"),
        ]);
        setList(b.data);
        setCentres(c.data);
        setWb(p.data);
        setWbSettings(s.data);
        if (c.data.length && !form.centre_id) setForm(f => ({ ...f, centre_id: c.data[0].id }));
    };
    useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

    const send = async (e) => {
        e.preventDefault();
        if (!form.title || !form.message) { toast.error("Title and message required"); return; }
        setSubmitting(true);
        try {
            const payload = { ...form };
            if (payload.audience !== "centre_visitors") delete payload.centre_id;
            await api.post("/admin/broadcasts", payload);
            toast.success("Broadcast queued (mock send)");
            setForm({ ...form, title: "", message: "" });
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setSubmitting(false);
        }
    };

    const runWinback = async () => {
        if (!confirm(`Send win-back broadcast to ${wb?.count || 0} lapsed players?`)) return;
        setWbRunning(true);
        try {
            const { data } = await api.post("/admin/winback/run");
            toast.success(`Win-back broadcast sent to ${data.recipients} players`);
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setWbRunning(false);
        }
    };

    const saveWbSettings = async (e) => {
        e.preventDefault();
        try {
            await api.put("/admin/winback/settings", wbSettings);
            toast.success("Win-back settings saved");
            setWbOpen(false);
            fetchAll();
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Failed to save");
        }
    };

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <h1 className="font-display text-3xl font-bold uppercase text-white">WhatsApp Broadcasts</h1>
                <p className="text-slate-400 text-sm mt-1">Reach your players where they actually read messages.</p>
                <div className="mt-2 inline-flex items-center gap-2 px-3 h-7 rounded-full bg-amber-950/50 border border-amber-700/60 text-amber-300 text-[10px] font-bold uppercase tracking-widest">
                    <Megaphone className="w-3 h-3" /> Mocked — Wati / AiSensy not yet wired
                </div>

                {/* Win-back card */}
                <div className="mt-6 bg-gradient-to-br from-pink-950/40 to-slate-900 border border-pink-900/40 rounded-lg p-5" data-testid="winback-card">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex items-start gap-3">
                            <div className="w-12 h-12 rounded-lg bg-pink-500/15 border border-pink-500/40 flex items-center justify-center flex-shrink-0">
                                <Heart className="w-6 h-6 text-pink-400" />
                            </div>
                            <div>
                                <h3 className="font-display text-xl font-bold uppercase text-white">Win-back Auto-Marketing</h3>
                                <p className="text-sm text-slate-400 mt-1 max-w-2xl">
                                    Automatically reach players inactive for {wbSettings?.days_threshold ?? 30}+ days with a personalized WhatsApp using their favourite sport + a one-time {wbSettings?.discount_percent ?? 15}% coupon ({wbSettings?.coupon_code ?? "WINBACK15"}).
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button onClick={() => setWbOpen(true)} data-testid="winback-settings-btn"
                                className="inline-flex items-center gap-1 px-3 h-9 bg-slate-900 border border-slate-700 hover:border-pink-500 text-slate-300 hover:text-pink-300 text-xs font-bold uppercase tracking-widest rounded">
                                <Settings className="w-3.5 h-3.5" /> Settings
                            </button>
                            <button onClick={runWinback} disabled={wbRunning || (wb?.count || 0) === 0}
                                data-testid="winback-run-btn"
                                className="inline-flex items-center gap-2 px-4 h-9 bg-pink-500 text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-pink-400 disabled:opacity-40 disabled:cursor-not-allowed">
                                <Play className="w-3.5 h-3.5" /> {wbRunning ? "Sending..." : `Run Now (${wb?.count || 0})`}
                            </button>
                        </div>
                    </div>

                    {/* Stats + last run */}
                    <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <Stat label="Lapsed players" value={wb?.count ?? "..."} accent="pink" />
                        <Stat label="Threshold" value={`${wbSettings?.days_threshold ?? 30}d`} />
                        <Stat label="Discount" value={`${wbSettings?.discount_percent ?? 15}%`} accent="green" />
                        <Stat label="Last run" value={wb?.last_run ? new Date(wb.last_run.created_at).toLocaleDateString() : "Never"} />
                    </div>

                    {wb?.sample?.length > 0 && (
                        <div className="mt-5 bg-slate-950/60 border border-slate-800 rounded-lg p-4">
                            <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-slate-500 mb-2 inline-flex items-center gap-1">
                                <Eye className="w-3 h-3" /> Sample candidates
                            </div>
                            <ul className="space-y-1.5">
                                {wb.sample.map((s, i) => (
                                    <li key={i} className="flex items-center justify-between text-xs text-slate-300" data-testid={`wb-sample-${i}`}>
                                        <span><b className="text-white">{s.name}</b> · {s.phone}</span>
                                        <span className="text-pink-400 font-bold uppercase tracking-widest">{s.fav_sport}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Composer */}
                    <form onSubmit={send} className="bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-3" data-testid="broadcast-form">
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Title (internal)</label>
                            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
                                placeholder="Weekend offer reminder" data-testid="bc-title"
                                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Message</label>
                            <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} required rows={5} maxLength={1024}
                                placeholder="Hey {{name}}, weekend slots are 10% off! Use code WEEKEND10."
                                data-testid="bc-message"
                                className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                            <div className="mt-1 text-[10px] text-slate-500">{form.message.length}/1024</div>
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Audience</label>
                            <select value={form.audience} onChange={e => setForm({ ...form, audience: e.target.value })}
                                data-testid="bc-audience"
                                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white">
                                <option value="all">All players</option>
                                <option value="active_30d">Active in last 30 days</option>
                                <option value="centre_visitors">Visitors of a specific centre</option>
                            </select>
                        </div>
                        {form.audience === "centre_visitors" && (
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Centre</label>
                                <select value={form.centre_id} onChange={e => setForm({ ...form, centre_id: e.target.value })}
                                    className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white">
                                    {centres.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                        )}
                        <button type="submit" disabled={submitting} data-testid="send-broadcast-btn"
                            className="w-full h-11 inline-flex items-center justify-center gap-2 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52] disabled:opacity-50 glow-green">
                            <Send className="w-4 h-4" /> {submitting ? "Sending..." : "Send Broadcast"}
                        </button>
                    </form>

                    {/* History */}
                    <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
                        <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-4">Recent broadcasts</div>
                        {list.length === 0 ? (
                            <div className="text-slate-500 text-sm text-center py-6">No broadcasts sent yet.</div>
                        ) : (
                            <ul className="space-y-3 max-h-96 overflow-y-auto pr-1">
                                {list.map(b => (
                                    <li key={b.id} className="bg-slate-950 border border-slate-800 rounded p-3" data-testid={`broadcast-${b.id}`}>
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="font-bold text-white text-sm flex items-center gap-2">
                                                {b.audience === "winback" && <Heart className="w-3.5 h-3.5 text-pink-400" />}
                                                {b.title}
                                            </div>
                                            <span className="text-[10px] uppercase tracking-widest text-[#00FF66] font-bold whitespace-nowrap">{b.recipients} recipients</span>
                                        </div>
                                        <div className="text-xs text-slate-300 mt-1 line-clamp-2">{b.message}</div>
                                        <div className="text-[10px] text-slate-500 mt-2 uppercase tracking-widest">{b.audience} · {new Date(b.created_at).toLocaleString()}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Settings modal */}
                {wbOpen && wbSettings && (
                    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                        <form onSubmit={saveWbSettings} className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="winback-settings-form">
                            <h3 className="font-display text-2xl font-bold uppercase text-white">Win-back Settings</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <SetField label="Days threshold" type="number" value={wbSettings.days_threshold}
                                    onChange={v => setWbSettings({ ...wbSettings, days_threshold: parseInt(v) || 30 })} testid="wb-days" />
                                <SetField label="Discount %" type="number" value={wbSettings.discount_percent}
                                    onChange={v => setWbSettings({ ...wbSettings, discount_percent: parseInt(v) || 15 })} testid="wb-discount" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <SetField label="Coupon code" value={wbSettings.coupon_code}
                                    onChange={v => setWbSettings({ ...wbSettings, coupon_code: v.toUpperCase() })} testid="wb-coupon" />
                                <SetField label="Coupon max uses" type="number" value={wbSettings.coupon_max_uses}
                                    onChange={v => setWbSettings({ ...wbSettings, coupon_max_uses: parseInt(v) || 5000 })} testid="wb-max" />
                            </div>
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Message template</label>
                                <textarea value={wbSettings.message_template}
                                    onChange={e => setWbSettings({ ...wbSettings, message_template: e.target.value })}
                                    rows={5} maxLength={1000} required data-testid="wb-template"
                                    className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-pink-500 font-mono text-xs" />
                                <p className="mt-1 text-[10px] text-slate-500">Available placeholders: {"{name}"} · {"{fav_sport}"} · {"{coupon}"} · {"{discount}"}</p>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => setWbOpen(false)} className="flex-1 h-11 border border-slate-700 rounded text-slate-300">Cancel</button>
                                <button type="submit" data-testid="save-wb-settings-btn" className="flex-1 h-11 bg-pink-500 text-black font-bold uppercase tracking-widest rounded">Save</button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}

function Stat({ label, value, accent }) {
    const cls = accent === "pink" ? "text-pink-400" : accent === "green" ? "text-[#00FF66]" : "text-white";
    return (
        <div className="bg-slate-950/60 border border-slate-800 rounded p-3">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">{label}</div>
            <div className={`mt-1 font-display text-lg sm:text-xl font-bold ${cls}`}>{value}</div>
        </div>
    );
}

function SetField({ label, type = "text", value, onChange, testid }) {
    return (
        <div>
            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">{label}</label>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required data-testid={testid}
                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-pink-500" />
        </div>
    );
}
